import React from 'react';
import { Card, CardContent, alpha } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Анимированная карточка с Framer Motion
const MotionCard = motion(Card);

// Стилизованная карточка с современным дизайном
const StyledCard = styled(MotionCard)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  borderRadius: theme.spacing(2),
  boxShadow: `
    0 4px 6px -1px ${alpha(theme.palette.common.black, 0.1)},
    0 2px 4px -1px ${alpha(theme.palette.common.black, 0.06)}
  `,
  margin: theme.spacing(3, 'auto'),
  width: '90%',
  overflow: 'visible',
  transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `
      0 10px 15px -3px ${alpha(theme.palette.common.black, 0.1)},
      0 4px 6px -2px ${alpha(theme.palette.common.black, 0.05)}
    `,
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

// Контент карточки
const StyledCardContent = styled(CardContent)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  overflow: 'visible',
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
}));

// Варианты анимации для контейнера
const containerVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    scale: 0.98,
    filter: 'blur(2px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94], // Smooth cubic bezier
      staggerChildren: 0.1
    }
  },
  exit: { 
    opacity: 0, 
    y: 10,
    scale: 0.98,
    filter: 'blur(2px)',
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  hover: {
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const Container = React.forwardRef(({ 
  children, 
  sx = {}, 
  animate = true,
  variant = 'animate',
  delay = 0,
  ...props 
}, ref) => {
  const animationProps = animate ? {
    variants: containerVariants,
    initial: "initial",
    animate: variant,
    exit: "exit",
    ...(delay > 0 && {
      transition: { ...containerVariants.animate.transition, delay }
    })
  } : {};

  return (
    <StyledCard
      ref={ref}
      sx={sx}
      {...animationProps}
      {...props}
    >
      <StyledCardContent>
        {children}
      </StyledCardContent>
    </StyledCard>
  );
});

export default Container;